import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import { ThrowError } from "@/api/callApi";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import FullPageLoader from "@/components/common/FullPageLoader";

const AddSalaryHead: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isBase, setIsBase] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<ISalaryHead>({
  });

  const selectedFrequency = watch("frequency");

  useEffect(() => {
    if (selectedFrequency === "monthly") {
      setValue("payableIn", "monthly");
    }
  }, [selectedFrequency, setValue]);

  useEffect(() => {
    if (!isEdit) return;


    const fetchData = async () => {
      setIsFetching(true);
      try {
        const response = await SalaryHeadServices.getById(id!);
        if (response.status && response.data) {
          const data = response.data as ISalaryHead;
          reset({
            name: data.name || "",
            type: data.type?.toLowerCase() || "",
            amountType: data.amountType || "value",
            taxExempted: data.taxExempted ?? false,
            frequency: data.frequency || "monthly",
            payableIn: data.payableIn || "monthly",
            fixVariable: data.fixVariable || "fixed",
            isActive: data.isActive ?? true,
            partOfForm16: data.partOfForm16 ?? true,
            wantToShowInOfferLetter: data.wantToShowInOfferLetter ?? false,
            fromEmployeer: data.fromEmployeer ?? false,
          } as any);
          setIsBase(data.isBase ?? false);
        }
      } catch (error) {
        ThrowError(error);
        navigate(-1);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [id, isEdit, reset, navigate]);

  const onSubmit = async (data: ISalaryHead) => {
    if (isBase) {
      showToastNotification("warning", "Base salary head cannot be edited");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: data.name.trim(),
        type: data.type,
        amountType: data.amountType,
        // isTaxable: data.isTaxable,
        taxExempted: data.taxExempted,
        frequency: data.frequency,
        payableIn: data.frequency === "monthly" ? "monthly" : data.payableIn,
        fixVariable: data.fixVariable,
        isActive: data.isActive,
        partOfForm16: data.partOfForm16,
        // maxCap: Number(data.maxCap) || 0,
        wantToShowInOfferLetter: data.wantToShowInOfferLetter,
        fromEmployeer: data.fromEmployeer,
        // fromEmployeer: data.fromEmployeer,
      };

      let response;
      if (isEdit) {
        response = await SalaryHeadServices.update(id!, payload);
      } else {
        response = await SalaryHeadServices.create(payload);
      }

      if (response.status) {
        showToastNotification(
          "success",
          response.message || (isEdit ? "Salary head updated successfully" : "Salary head created successfully")
        );
        navigate(RoutesLink.salaryHead);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title={isEdit ? (isBase ? "View Salary Head" : "Edit Salary Head") : "Add Salary Head"}
          isBackButtonVisible={true}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              <FormField
                {...register("name", { ...errorTypes.required("Name") })}
                type="text"
                label="Name"
                placeholder="read Name"
                error={errors.name?.message as string}
                disabled={isBase}
                required
              />


              <Controller
                name="type"
                control={control}
                rules={{ required: "Type is required" }}
                render={({ field }) => (
                  <FormField
                    {...field}
                    type="select"
                    label="Type"
                    placeholder="Select Head type"
                    options={[
                      { label: "Earning", value: "earning" },
                      { label: "Deduction", value: "deduction" },
                    ]}
                    error={errors.type?.message as string}
                    disabled={isBase}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    required
                  />
                )}
              />

              <Controller
                name="amountType"
                control={control}
                rules={{ required: "Amount type is required" }}
                render={({ field }) => (
                  <FormField
                    {...field}
                    type="select"
                    label="Amount Type"
                    placeholder="Select Amount type"
                    options={[
                      { label: "Value", value: "value" },
                      { label: "Percentage", value: "percentage" },
                    ]}
                    error={errors.amountType?.message as string}
                    disabled={isBase}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    required
                  />
                )}
              />

              <Controller
                name="fixVariable"
                control={control}
                rules={{ required: "Fixed/Variable is required" }}
                render={({ field }) => (
                  <FormField
                    {...field}
                    type="select"
                    label="Fixed / Variable"
                    placeholder="Select Fixed/Variable"
                    options={[
                      { label: "All", value: "" },
                      { label: "Fixed", value: "fixed" },
                      { label: "Variable", value: "variable" },
                    ]}
                    error={errors.fixVariable?.message as string}
                    disabled={isBase}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    required
                  />
                )}
              />

              <Controller
                name="frequency"
                control={control}
                rules={{ required: "Frequency is required" }}
                render={({ field }) => (
                  <FormField
                    {...field}
                    type="select"
                    label="Frequency"
                    placeholder="Select Frequency"
                    options={[

                      { label: "Select Frequency", value: "" },
                      { label: "Monthly", value: "monthly" },
                      { label: "Yearly", value: "yearly" },
                      { label: "On Claim", value: "onClaim" },
                      { label: "On Requirement", value: "onRequirement" },
                    ]}
                    error={errors.frequency?.message as string}
                    disabled={isBase}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    required
                  />
                )}
              />

              {selectedFrequency === "yearly" && (
                <Controller
                  name="payableIn"
                  control={control}
                  rules={{ required: "Payable Month is required" }}
                  render={({ field }) => (
                    <FormField
                      {...field}
                      type="select"
                      label="Payable Month"
                      placeholder="Select Payable Month"
                      options={[
                        { label: "Select Payable Month", value: "" },
                        { label: "January", value: "january" },
                        { label: "February", value: "february" },
                        { label: "March", value: "march" },
                        { label: "April", value: "april" },
                        { label: "May", value: "may" },
                        { label: "June", value: "june" },
                        { label: "July", value: "july" },
                        { label: "August", value: "august" },
                        { label: "September", value: "september" },
                        { label: "October", value: "october" },
                        { label: "November", value: "november" },
                        { label: "December", value: "december" },
                      ]}
                      error={errors.payableIn?.message as string}
                      disabled={isBase}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      required
                    />
                  )}
                />
              )}
              {/* {(selectedFrequency === "onClaim" || selectedFrequency === "onRequirement") && (
                <FormField
                  {...register("payableIn", {
                    required: "Payable is required",
                  })}
                  type="text"
                  label="Value *"
                  placeholder="Enter Your Value"
                  error={errors.payableIn?.message as string}
                  disabled={isBase}
                />
              )} */}

              {/* <FormField
                {...register("maxCap", {
                  required: "Max Cap is required",
                  validate: (val) => !isNaN(Number(val)) || "Must be a valid number",
                })}
                type="number"
                label="Max Cap *"
                placeholder="Enter Max Cap"
                error={errors.maxCap?.message as string}
                disabled={isBase}
              /> */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 border-t border-gray-100 pt-6">
              <div className="flex items-center space-x-4">
                <Switch
                  id="wantToShowInOfferLetter"
                  checked={watch("wantToShowInOfferLetter")}
                  onCheckedChange={(checked) => setValue("wantToShowInOfferLetter", checked)}
                  disabled={isBase}
                />
                <label htmlFor="wantToShowInOfferLetter" className="text-sm font-medium leading-none">
                  Show in Offer Letter
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <Switch
                  id="fromEmployeer"
                  checked={watch("fromEmployeer")}
                  onCheckedChange={(checked) => setValue("fromEmployeer", checked)}
                  disabled={isBase}
                />
                <label htmlFor="fromEmployeer" className="text-sm font-medium leading-none">
                  From Employee
                </label>
              </div>
              <div className="flex items-center space-x-4">
                <Switch
                  id="taxExempted"
                  checked={watch("taxExempted")}
                  onCheckedChange={(checked) => setValue("taxExempted", checked)}
                  disabled={isBase}
                />
                <label htmlFor="taxExempted" className="text-sm font-medium leading-none">
                  Tax Exempted
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <Switch
                  id="isActive"
                  checked={watch("isActive")}
                  onCheckedChange={(checked) => setValue("isActive", checked)}
                  disabled={isBase}
                />
                <label htmlFor="isActive" className="text-sm font-medium leading-none">
                  Is Active
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <Switch
                  id="partOfForm16"
                  checked={watch("partOfForm16")}
                  onCheckedChange={(checked) => setValue("partOfForm16", checked)}
                  disabled={isBase}
                />
                <label htmlFor="partOfForm16" className="text-sm font-medium leading-none">
                  Part of Form 16
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(RoutesLink.salaryHead)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              {!isBase && (
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                      ? "Update"
                      : "Create"}
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default AddSalaryHead;